import React from 'react';
import { CheckboxV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { HONEYMOON_TAG } from '@shared/constants/accommodations';

type PrioritizeTagsFacetProps = Readonly<{
  /** Tags offered as choices, in display order. Rendering is skipped entirely when empty. */
  availableTags: ReadonlyArray<string>;
  /** Tags the results are currently restricted to, read from `?prioritizeTag=`. */
  selectedTags: ReadonlyArray<string>;
  onToggle: (tag: string, isSelected: boolean) => void;
}>;

/**
 * Chooses which curated tags the results are restricted to — the UI over `?prioritizeTag=`.
 *
 * A filter: with a tag checked, only stays carrying it are shown (the server returns nothing
 * else), and unchecking it is the way back to the full page. Several checked tags are ANDed —
 * "Honeymoon stays" + "Beach" narrows to honeymoon stays on a beach — so every extra tick can only
 * shrink the page. It still sits above the ordinary
 * filters and "Clear all" leaves it alone (see FiltersPanel.onReset), because it is the page's
 * own premise on `/honeymoon` rather than a refinement of it.
 */
export const PrioritizeTagsFacet: React.FC<PrioritizeTagsFacetProps> = ({ availableTags, selectedTags, onToggle }) => {
  const { tq } = useTranslation('accommodationsConsole');

  if (!availableTags.length) {
    return null;
  }

  // Keyed off the tag rather than a label map so an unrecognized tag still renders something
  // honest instead of a blank row.
  const labelForTag = (tag: string): string => {
    switch (tag) {
      case HONEYMOON_TAG:
        return tq`Honeymoon stays`;
      case 'beach':
        return tq`Beach`;
      case 'wellness':
        return tq`Wellness`;
      case 'nature':
        return tq`Nature`;
      case 'luxury':
        return tq`Luxury`;
      case 'adventure':
        return tq`Adventure`;
      default:
        return tag;
    }
  };

  const selected = new Set(selectedTags);

  return (
    <Flex
      flexDirection="column"
      gap={3}
    >
      <TextV2 typographyVariant="hed2">{tq`Show only`}</TextV2>
      {availableTags.map(tag => (
        <CheckboxV2
          key={tag}
          id={`prioritize-tag-${tag}`}
          isChecked={selected.has(tag)}
          onChange={event => onToggle(tag, event.target.checked)}
        >
          <TextV2
            color="mono14"
            typographyVariant="body1"
          >
            {labelForTag(tag)}
          </TextV2>
        </CheckboxV2>
      ))}
      <TextV2
        color="mono12"
        typographyVariant="label2"
      >
        {tq`Only stays matching every checked tag are shown. Uncheck to see more hotels.`}
      </TextV2>
    </Flex>
  );
};
